/**
 * TasksDashboard - Página do Dashboard de Tarefas
 *
 * Exibe dashboards em formato de abas (Visão Geral, Minhas Tarefas).
 * Os filtros rápidos ficam no header, alinhados ao padrão CRM/Financeiro,
 * e são propagados ao componente do dashboard via prop `filters`.
 *
 * @version 3.1.0
 * RELEASE: 2026-04-06 v1.0.0 - Placeholder inicial
 * RELEASE: 2026-04-12 v2.0.0 - Dashboard funcional com abas e KPIs
 * RELEASE: 2026-04-18 v3.0.0 - Filtros rápidos no header (padrão CRM)
 * RELEASE: 2026-06-24 v3.1.0 - Botão "Nova Tarefa" no header (mesmas
 *          permissões da listagem) + dialogs de criação/sucesso/limite.
 */

import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, LayoutDashboard, RefreshCw, Plus } from 'lucide-react';
import { useModuleDashboards } from '@/hooks/useDashboards';
// Rótulos/rotas por módulo de tarefas (Tarefas | Retenção | Jurídico)
import { useTaskModule } from '@/hooks/useTaskModuleType';
import { getTaskModuleMenuKeys } from '@/lib/tasks/taskModuleMenuKeys';

import { DashboardTabs, DashboardRenderer } from '@/components/crm/dashboard';
import { Button } from '@/components/ui/button';
import { useRefreshWithTimestamp } from '@/hooks/useRefreshWithTimestamp';
import { cn } from '@/lib/utils';
import {
  TaskDashboardFilters,
  EMPTY_DASHBOARD_FILTERS,
  type TaskDashboardFiltersState,
} from '@/components/tasks/dashboard/TaskDashboardFilters';
import { MyTasksDashboardFilters } from '@/components/tasks/dashboard/MyTasksDashboardFilters';
import { TasksUsageInline } from '@/components/tasks/billing/TasksUsageInline';
// v3.1.0 — botão Nova Tarefa
import { usePermissions } from '@/hooks/usePermissions';
import { useTaskPhasePermissions } from '@/hooks/useTaskPhasePermissions';
import { TaskFormDialog } from '@/components/tasks/modal/TaskFormDialog';
import { TaskCreatedSuccessDialog } from '@/components/tasks/modal/TaskCreatedSuccessDialog';
import { LimitReachedModal } from '@/components/tenant/LimitReachedModal';
import { useTasksUsage } from '@/hooks/useTasksUsage';

/**
 * Página principal do Dashboard de Tarefas
 * Integra o sistema de abas com renderização dinâmica de dashboards
 */
export default function TasksDashboard() {
  const queryClient = useQueryClient();
  // Módulo corrente (Tarefas | Retenção | Jurídico) — apenas rótulos/rota de UI
  const taskModule = useTaskModule();

  /** Hook para refresh com timestamp */
  const { isRefreshing, triggerRefresh } = useRefreshWithTimestamp();

  /** Estado dos filtros rápidos (subido do componente para o header) */
  const [dashboardFilters, setDashboardFilters] = useState<TaskDashboardFiltersState>(
    EMPTY_DASHBOARD_FILTERS,
  );

  /**
   * Dashboards disponíveis do módulo corrente.
   * Retenção possui dashboards próprios ("Por Solicitações"); Tarefas e
   * Jurídico continuam usando os dashboards do módulo 'tasks'.
   */
  // Retenção e Jurídico possuem dashboards próprios; Tarefas usa o módulo 'tasks'
  const dashboardModule =
    taskModule.type === 'retention' ? 'retention' : taskModule.type === 'legal' ? 'legal' : 'tasks';
  const {
    dashboards,
    selectedDashboard,
    selectedDashboardId,
    selectDashboard,
    isLoading,
  } = useModuleDashboards(dashboardModule);

  // ============================================================
  // v3.1.0 — Permissões e estado do botão "Nova Tarefa"
  // (mesma regra usada em src/pages/tasks/TaskList.tsx)
  // ============================================================
  const { canCreate } = usePermissions();
  const { permissionsMap, isAdmin: isPhaseAdmin } = useTaskPhasePermissions();
  const canCreateInAnyPhase = useMemo(() => {
    if (isPhaseAdmin) return true;
    for (const [, perm] of permissionsMap) {
      if (perm.can_create) return true;
    }
    return false;
  }, [isPhaseAdmin, permissionsMap]);
  // 2026-10-22: chave do próprio módulo
  const canShowNewTask = canCreate(getTaskModuleMenuKeys(taskModule.type).list) && canCreateInAnyPhase;

  const { data: tasksUsage } = useTasksUsage();

  // Estados dos dialogs (espelho mínimo do que existe na listagem)
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [createdTaskNumber, setCreatedTaskNumber] = useState<number | null>(null);

  /** Atualizar dados do dashboard */
  const handleRefresh = useCallback(async () => {
    await triggerRefresh(async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['task-dashboard-overview'] }),
        queryClient.invalidateQueries({ queryKey: ['task-dashboard-tables'] }),
        queryClient.invalidateQueries({ queryKey: ['task-calendar-indicators'] }),
      ]);
    });
  }, [queryClient, triggerRefresh]);

  /** Estado de carregamento inicial */
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  /** Sem dashboards disponíveis */
  if (dashboards.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <LayoutDashboard className="h-16 w-16 text-muted-foreground/50" />
        <h1 className="text-2xl font-bold text-foreground">Dashboard de {taskModule.label}</h1>
        <p className="text-muted-foreground text-center max-w-md">
          Nenhum dashboard disponível. Verifique suas permissões de acesso.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ================================================================= */}
      {/* Header do Dashboard — mobile-first:                               */}
      {/*  - Linha 1: título + refresh + (push) ações (uso + Nova Tarefa)   */}
      {/*  - Linha 2 (mobile): filtros em largura total                     */}
      {/*  - Desktop: tudo na mesma linha (título | filtros | ações)        */}
      {/* ================================================================= */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2">
        {/* Linha 1 (mobile) / esquerda (desktop): título + refresh */}
        <div className="flex items-center gap-1.5 w-full sm:w-auto">
          <h1 className="text-lg md:text-2xl font-bold tracking-tight text-foreground shrink-0">
            {/* Mobile mostra apenas "Dashboard"; desktop mantém o nome completo */}
            <span className="sm:hidden">Dashboard</span>
            <span className="hidden sm:inline">Dashboard de {taskModule.label}</span>
          </h1>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="Atualizar"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', isRefreshing && 'animate-spin')} />
          </Button>
        </div>

        {/* Separador visual (apenas desktop) */}
        <span className="text-muted-foreground/30 shrink-0 hidden sm:inline">|</span>

        {/* Filtros — abaixo no mobile (full width), inline no desktop */}
        <div className="w-full sm:w-auto sm:flex-1 sm:min-w-0">
          {selectedDashboard?.component_key === 'tasks_my_tasks' ? (
            <MyTasksDashboardFilters
              filters={dashboardFilters}
              onFiltersChange={setDashboardFilters}
            />
          ) : (
            <TaskDashboardFilters
              filters={dashboardFilters}
              onFiltersChange={setDashboardFilters}
            />
          )}
        </div>

        {/* Ações (uso + Nova Tarefa) — após os filtros */}
        <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto justify-end">
          <TasksUsageInline />
          {canShowNewTask && (
            <Button
              size="xs"
              onClick={() => setShowCreateDialog(true)}
              title={taskModule.newLabel}
              className="hover:shadow-md hover:scale-[1.02] transition-all duration-200"
            >
              <Plus className="sm:mr-1.5" />
              <span className="hidden sm:inline">{taskModule.newLabel}</span>
            </Button>
          )}
        </div>
      </div>



      {/* ================================================================= */}
      {/* Abas de dashboards */}
      {/* ================================================================= */}
      {dashboards.length > 1 && (
        <DashboardTabs
          dashboards={dashboards}
          selectedDashboardId={selectedDashboardId}
          onSelectDashboard={selectDashboard}
        />
      )}

      {/* ================================================================= */}
      {/* Renderizador de dashboard — recebe os filtros do header           */}
      {/* ================================================================= */}
      {selectedDashboard && (
        <DashboardRenderer
          componentKey={selectedDashboard.component_key}
          dashboardId={selectedDashboard.id}
          filters={dashboardFilters as any}
        />
      )}

      {/* ============================================================ */}
      {/* v3.1.0 — Dialog de criação de tarefa (mesmo da Listagem)     */}
      {/* ============================================================ */}
      <TaskFormDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onLimitReached={() => setLimitModalOpen(true)}
        onSuccess={(_taskId, taskNumber) => {
          setShowCreateDialog(false);
          // Atualiza caches relevantes ao Dashboard e à Listagem
          queryClient.invalidateQueries({ queryKey: ['task-dashboard-overview'] });
          queryClient.invalidateQueries({ queryKey: ['task-dashboard-tables'] });
          queryClient.invalidateQueries({ queryKey: ['task-calendar-indicators'] });
          queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
          queryClient.invalidateQueries({ queryKey: ['tasks-usage'] });
          if (taskNumber) {
            setCreatedTaskNumber(taskNumber);
            setShowSuccessDialog(true);
          }
        }}
      />

      {/* Modal de limite mensal — disparado pelo trigger do banco */}
      <LimitReachedModal
        open={limitModalOpen}
        onOpenChange={setLimitModalOpen}
        resourceType="message"
        currentCount={tasksUsage?.tasks_created_month}
        maxAllowed={tasksUsage?.tasks_limit}
        customMessage="Você atingiu o limite mensal de tarefas do seu plano. Contrate um pack para continuar criando tarefas neste mês."
      />

      {/* Sucesso — botão "Ver tarefa" leva à Listagem com o id na URL */}
      <TaskCreatedSuccessDialog
        open={showSuccessDialog}
        onOpenChange={setShowSuccessDialog}
        taskNumber={createdTaskNumber}
        onViewTask={() => {
          setShowSuccessDialog(false);
          // Dashboard não tem modal de detalhes; encaminha à listagem.
          if (createdTaskNumber) {
            window.location.href = `${taskModule.basePath}/list?taskNumber=${createdTaskNumber}`;
          }
        }}
      />
    </div>
  );
}
